import { useEffect, useRef, useState } from 'react';
import { ChevronDown, Search } from 'lucide-react';
import { normalizarBusca } from '../../utils/membros';

/**
 * "Comparar": escolhe quais itens (pessoas ou credenciais) aparecem no gráfico, quantos quiser.
 * Fecha com Esc e com clique fora. Com muitos itens aparece uma busca.
 */
const ITENS_PARA_MOSTRAR_BUSCA = 6;

export function SeletorDeItens({
  itens,
  visiveis,
  aoMudar,
  nomeDoItem,
}: {
  itens: { id: string; rotulo: string; detalhe?: string | null }[];
  /** ids marcados (os que aparecem no gráfico). */
  visiveis: Set<string>;
  aoMudar: (visiveis: Set<string>) => void;
  /** "pessoas", "credenciais": usado no texto do botão. */
  nomeDoItem: string;
}) {
  const [aberto, setAberto] = useState(false);
  const [busca, setBusca] = useState('');
  const raizRef = useRef<HTMLDivElement>(null);
  const botaoRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!aberto) return;
    const aoClicarFora = (e: MouseEvent) => {
      if (raizRef.current && !raizRef.current.contains(e.target as Node)) setAberto(false);
    };
    const aoTeclar = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        setAberto(false);
        botaoRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', aoClicarFora);
    document.addEventListener('keydown', aoTeclar);
    return () => {
      document.removeEventListener('mousedown', aoClicarFora);
      document.removeEventListener('keydown', aoTeclar);
    };
  }, [aberto]);

  const marcados = itens.filter((i) => visiveis.has(i.id)).length;
  const termo = normalizarBusca(busca);
  const filtrados = termo ? itens.filter((i) => normalizarBusca(`${i.rotulo} ${i.detalhe ?? ''}`).includes(termo)) : itens;

  const alternar = (id: string) => {
    const proximos = new Set(visiveis);
    if (proximos.has(id)) proximos.delete(id);
    else proximos.add(id);
    aoMudar(proximos);
  };

  return (
    <div ref={raizRef} style={{ position: 'relative', flexShrink: 0 }}>
      <button
        ref={botaoRef}
        type="button"
        className="btn btn--outline btn--sm"
        aria-haspopup="true"
        aria-expanded={aberto}
        onClick={() => setAberto((v) => !v)}
      >
        Comparar {marcados === itens.length ? `(todas as ${itens.length})` : `(${marcados} de ${itens.length})`}
        <ChevronDown size={14} />
      </button>

      {aberto && (
        <div
          role="dialog"
          aria-label={`Escolher ${nomeDoItem} para comparar`}
          style={{ position: 'absolute', top: 'calc(100% + 6px)', right: 0, width: 'min(300px, calc(100vw - 48px))', background: 'var(--color-bg-surface)', border: '1px solid var(--color-border-strong)', borderRadius: 'var(--radius-md)', boxShadow: 'var(--shadow-lg)', padding: 'var(--space-3)', zIndex: 20, display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}
        >
          {itens.length > ITENS_PARA_MOSTRAR_BUSCA && (
            <div className="campo-busca" style={{ flex: 'none' }}>
              <input type="search" value={busca} onChange={(e) => setBusca(e.target.value)} placeholder="Buscar" aria-label={`Buscar ${nomeDoItem}`} autoFocus />
              <Search size={16} />
            </div>
          )}
          <div style={{ display: 'flex', gap: 'var(--space-3)', fontSize: 'var(--text-xs)' }}>
            <button type="button" onClick={() => aoMudar(new Set(itens.map((i) => i.id)))} style={{ color: 'var(--color-primary-400)', fontWeight: 'var(--font-medium)', minHeight: 28 }}>
              Marcar todas
            </button>
            <button type="button" onClick={() => aoMudar(new Set())} style={{ color: 'var(--color-text-secondary)', minHeight: 28 }}>
              Desmarcar todas
            </button>
          </div>
          <ul style={{ listStyle: 'none', margin: 0, padding: 0, maxHeight: 260, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
            {filtrados.map((item) => (
              <li key={item.id}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', minHeight: 36, padding: '0 var(--space-1)', borderRadius: 'var(--radius-sm)', cursor: 'pointer', fontSize: 'var(--text-sm)' }}>
                  <input
                    type="checkbox"
                    checked={visiveis.has(item.id)}
                    onChange={() => alternar(item.id)}
                    style={{ width: 16, height: 16, accentColor: 'var(--color-primary-500)', flexShrink: 0 }}
                  />
                  <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={item.detalhe ? `${item.rotulo} · ${item.detalhe}` : item.rotulo}>
                    {item.rotulo}
                    {item.detalhe && <span style={{ color: 'var(--color-text-secondary)' }}> · {item.detalhe}</span>}
                  </span>
                </label>
              </li>
            ))}
            {filtrados.length === 0 && (
              <li style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-secondary)', padding: 'var(--space-2) var(--space-1)' }}>Nada encontrado.</li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
